/**
 * Course editor.
 *
 * The whole course is one document: edit it here, save it in one go. Partial
 * saves would need change-tracking across five levels of nesting to buy
 * nothing — the body is stored as a document, not a tree of rows.
 *
 * Objectives matter more than they look: they are what the AI marks against,
 * so a lesson with none can be published but will never assess anything. The
 * editor says so rather than letting a teacher discover it from a learner.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiError, apiGet, apiPut } from "../../api/client";

type Objective = { id: string; description: string; bloom_level: string; keywords: string[] };
type Section = {
  id: string; title: string; content: string; order: number;
  key_concepts: string[]; examples: string[]; figures: unknown[]; objectives: Objective[];
};
type Lesson = {
  id: string; title: string; description: string; difficulty: string;
  estimated_minutes: number; prerequisites: string[]; tags: string[]; sections: Section[];
};
type Course = { id: string; title: string; description: string; lessons: Lesson[] };

const BLOOM = ["remember", "understand", "apply", "analyze", "evaluate", "create"];

export function CourseEditorPage() {
  const { cid } = useParams<{ cid: string }>();
  const [course, setCourse] = useState<Course | null>(null);
  const [notice, setNotice] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["course", cid],
    queryFn: () => apiGet<Course>(`/api/courses/${cid}`),
  });

  useEffect(() => {
    if (data) setCourse(data);
  }, [data]);

  const save = useMutation({
    mutationFn: (c: Course) => apiPut(`/api/courses/${cid}`, { curriculum: c }),
    onSuccess: () => setNotice("Saved."),
    onError: (e: ApiError) => setNotice(e.message),
  });

  if (isLoading) return <p className="text-muted">Loading course…</p>;
  if (error || !course)
    return <p className="error-message">Course not found, or not yours to edit.</p>;

  const edit = (fn: (draft: Course) => void) => {
    const next = structuredClone(course) as Course;
    fn(next);
    setCourse(next);
    setNotice("");
  };

  const addLesson = () =>
    edit((c) => {
      c.lessons.push({
        id: `lesson-${c.lessons.length + 1}-${Math.random().toString(36).slice(2, 6)}`,
        title: "New lesson",
        description: "",
        difficulty: "intermediate",
        estimated_minutes: 30,
        prerequisites: [],
        tags: [],
        sections: [],
      });
    });

  const addSection = (li: number) =>
    edit((c) => {
      const l = c.lessons[li];
      l.sections.push({
        id: `${l.id}-s${l.sections.length + 1}`,
        title: "New section",
        content: "",
        order: l.sections.length + 1,
        key_concepts: [],
        examples: [],
        figures: [],
        objectives: [],
      });
    });

  const addObjective = (li: number, si: number) =>
    edit((c) => {
      const s = c.lessons[li].sections[si];
      s.objectives.push({
        id: `${s.id}-o${s.objectives.length + 1}`,
        description: "",
        bloom_level: "understand",
        keywords: [],
      });
    });

  const objectiveCount = course.lessons.reduce(
    (n, l) => n + l.sections.reduce((m, s) => m + s.objectives.length, 0),
    0,
  );

  return (
    <div className="course-editor">
      <Link to="/tutor/courses" className="breadcrumb-link">← My courses</Link>
      <h1>{course.title || "Untitled course"}</h1>

      {notice && <div className="notice" role="status">{notice}</div>}
      {objectiveCount === 0 && (
        <div className="follow-up-hint">
          No objectives yet. The AI marks answers against objectives, so a course
          without them cannot assess anything.
        </div>
      )}

      <div className="card">
        <label>
          <span className="field-label">Course title</span>
          <input
            value={course.title}
            onChange={(e) => edit((c) => { c.title = e.target.value; })}
          />
        </label>
        <label style={{ marginTop: 12, display: "block" }}>
          <span className="field-label">Description</span>
          <textarea
            rows={2}
            value={course.description}
            onChange={(e) => edit((c) => { c.description = e.target.value; })}
          />
        </label>
      </div>

      {course.lessons.map((lesson, li) => (
        <div key={lesson.id} className="card editor-lesson">
          <div className="editor-row">
            <label style={{ flex: 1 }}>
              <span className="field-label">Lesson {li + 1}</span>
              {/* The id is fixed at creation and never follows the title.
                  It was derived from the title here, which changed the id on
                  every keystroke — and the id is this row's React key, so the
                  field was destroyed and rebuilt per character and lost focus.
                  It is also what progress rows reference: renaming a lesson
                  would have orphaned every learner's progress on it. */}
              <input
                value={lesson.title}
                onChange={(e) =>
                  edit((c) => {
                    c.lessons[li].title = e.target.value;
                  })
                }
              />
            </label>
            <label style={{ width: 130 }}>
              <span className="field-label">Minutes</span>
              <input
                type="number"
                min={5}
                value={lesson.estimated_minutes}
                onChange={(e) =>
                  edit((c) => {
                    c.lessons[li].estimated_minutes = Number(e.target.value) || 30;
                  })
                }
              />
            </label>
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => edit((c) => { c.lessons.splice(li, 1); })}
            >
              Remove lesson
            </button>
          </div>

          {lesson.sections.map((section, si) => (
            <div key={section.id} className="editor-section">
              <div className="editor-row">
                <label style={{ flex: 1 }}>
                  <span className="field-label">Section</span>
                  <input
                    value={section.title}
                    onChange={(e) =>
                      edit((c) => { c.lessons[li].sections[si].title = e.target.value; })
                    }
                  />
                </label>
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => edit((c) => { c.lessons[li].sections.splice(si, 1); })}
                >
                  Remove
                </button>
              </div>
              <label>
                <span className="field-label">Teaching content</span>
                <textarea
                  rows={4}
                  value={section.content}
                  placeholder="What the learner reads before the questions."
                  onChange={(e) =>
                    edit((c) => { c.lessons[li].sections[si].content = e.target.value; })
                  }
                />
              </label>

              <span className="field-label" style={{ marginTop: 10, display: "block" }}>
                Objectives — what gets marked
              </span>
              {section.objectives.map((o, oi) => (
                <div key={o.id} className="editor-objective">
                  <input
                    value={o.description}
                    placeholder="e.g. Differentiate a polynomial from first principles"
                    onChange={(e) =>
                      edit((c) => {
                        c.lessons[li].sections[si].objectives[oi].description =
                          e.target.value;
                      })
                    }
                  />
                  <select
                    value={o.bloom_level}
                    onChange={(e) =>
                      edit((c) => {
                        c.lessons[li].sections[si].objectives[oi].bloom_level =
                          e.target.value;
                      })
                    }
                  >
                    {BLOOM.map((b) => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() =>
                      edit((c) => {
                        c.lessons[li].sections[si].objectives.splice(oi, 1);
                      })
                    }
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => addObjective(li, si)}
              >
                + Objective
              </button>
            </div>
          ))}

          <button className="btn btn-sm btn-secondary" onClick={() => addSection(li)}>
            + Section
          </button>
        </div>
      ))}

      <div className="editor-actions">
        <button className="btn btn-secondary" onClick={addLesson}>
          + Lesson
        </button>
        <button
          className="btn btn-primary"
          disabled={save.isPending}
          onClick={() => save.mutate(course)}
        >
          {save.isPending ? "Saving…" : "Save course"}
        </button>
      </div>
    </div>
  );
}
